import React, { useContext, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import CredentialItem from "./CredentialItem";
import CredentialsContext from "../context/CredentialsContext";
import {
    expirationLabels,
    getExpirationStatus,
} from "../utils/expiration";

export default function CredentialsGrid() {
    const { credentials, loading, error, refresh } = useContext(CredentialsContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const searchTerm = searchParams.get("q") || "";
    const filterValues = ["all", "active", "expiring-soon", "expired", "no-expiration"];
    const expirationFilter = filterValues.includes(searchParams.get("status"))
        ? searchParams.get("status") : "all";
    const [sortOrder, setSortOrder] = useState("company");
    const updateFilter = (key, value) => {
        setSearchParams((previous) => {
            const next = new URLSearchParams(previous);
            if (value && !(key === "status" && value === "all")) next.set(key, value);
            else next.delete(key);
            return next;
        }, { replace: true });
    };
    const clearFilters = () => setSearchParams({}, { replace: true });
    const hasFilters = Boolean(searchTerm || expirationFilter !== "all");

    const searchedCredentials = useMemo(() => {
        return credentials.filter((credential) => {
            const company = (credential.company || "").toLowerCase();
            const provider = (credential.provider || "").toLowerCase();
            const term = searchTerm.trim().toLowerCase();
            const matchesText = company.includes(term) || provider.includes(term);
            if (!matchesText) {
                return false;
            }
            if (expirationFilter === "all") {
                return true;
            }
            const status = getExpirationStatus(credential.expires_at);
            return status === expirationFilter;
        }).sort((a, b) => {
            if (sortOrder === "expiry") {
                const dateA = a.expires_at ? new Date(a.expires_at).getTime() : Infinity;
                const dateB = b.expires_at ? new Date(b.expires_at).getTime() : Infinity;
                if (dateA !== dateB) return dateA - dateB;
            }
            return (a.company || "").localeCompare(b.company || "", undefined, { sensitivity: "base" })
                || (a.provider || "").localeCompare(b.provider || "");
        });
    }, [credentials, searchTerm, expirationFilter, sortOrder]);

    const expirationSummary = useMemo(() => {
        const summary = {
            expired: 0,
            "expiring-soon": 0,
            active: 0,
            "no-expiration": 0,
        };
        credentials.forEach((credential) => {
            const status = getExpirationStatus(credential.expires_at);
            if (summary[status] !== undefined) {
                summary[status] += 1;
            }
        });
        return summary;
    }, [credentials]);

    const showWarning =
        expirationSummary.expired > 0 ||
        expirationSummary["expiring-soon"] > 0;

    return (
        <div className="credentials-grid" aria-busy={loading}>
            <div className="vault-toolbar">
            <div className="credentials-search">
                <label htmlFor="search-credentials">Search credentials</label>
                <input
                    name="search-credentials"
                    id="search-credentials"
                    type="search"
                    className="search-input"
                    placeholder="Search by company or provider…"
                    onChange={(e) => updateFilter("q", e.target.value)}
                    value={searchTerm}
                />
            </div>
                <div className="credentials-sort">
                    <label htmlFor="credential-sort">Sort by</label>
                    <select id="credential-sort" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                        <option value="company">Company A–Z</option>
                        <option value="expiry">Expiring first</option>
                    </select>
                </div>
            </div>
            {!loading && !error && <div className="credentials-expiration-summary">
                <div className="summary-item">
                    <span className="summary-label">Active</span>
                    <span className="summary-value">
                        {expirationSummary.active}
                    </span>
                </div>
                <div className="summary-item warning">
                    <span className="summary-label">Expiring soon</span>
                    <span className="summary-value">
                        {expirationSummary["expiring-soon"]}
                    </span>
                </div>
                <div className="summary-item danger">
                    <span className="summary-label">Expired</span>
                    <span className="summary-value">
                        {expirationSummary.expired}
                    </span>
                </div>
                <div className="summary-item muted">
                    <span className="summary-label">No expiration</span>
                    <span className="summary-value">
                        {expirationSummary["no-expiration"]}
                    </span>
                </div>
            </div>}
            <div className="credentials-expiration-filters" role="group" aria-label="Filter by expiration">
                {[
                    { value: "all", label: "All" },
                    { value: "active", label: expirationLabels.active },
                    {
                        value: "expiring-soon",
                        label: expirationLabels["expiring-soon"],
                    },
                    { value: "expired", label: expirationLabels.expired },
                    {
                        value: "no-expiration",
                        label: expirationLabels["no-expiration"],
                    },
                ].map((filter) => (
                    <button
                        key={filter.value}
                        type="button"
                        className={`filter-chip ${
                            expirationFilter === filter.value ? "active" : ""
                        }`}
                        aria-pressed={expirationFilter === filter.value}
                        onClick={() => updateFilter("status", filter.value)}
                    >
                        {filter.label}
                    </button>
                ))}
            </div>
            {!loading && !error && showWarning && (
                <div className="expiration-warning-banner">
                    <span>{expirationSummary.expired} expired · {expirationSummary["expiring-soon"]} expiring soon.
                        {" "}Review these logins before using them.</span>
                </div>
            )}
            {loading ? (
                <div className="vault-empty" role="status">Loading credentials…</div>
            ) : error ? (
                <div className="vault-empty" role="alert">
                    <h2>We couldn’t load your credentials</h2>
                    <p>Check your connection and try again.</p>
                    <button type="button" className="btn" onClick={refresh}>Try again</button>
                </div>
            ) : (
                <>
                    <div className="results-toolbar">
                        <p role="status">{searchedCredentials.length} of {credentials.length} credentials</p>
                        {hasFilters && <button type="button" className="text-button" onClick={clearFilters}>Clear filters</button>}
                    </div>
                    {searchedCredentials.length === 0 ? (
                        <div className="vault-empty">
                            <span className="empty-icon" aria-hidden="true">⌕</span>
                            <h2>{credentials.length ? "No matching credentials" : "Your vault is ready"}</h2>
                            <p>{credentials.length ? "Try another company, provider or expiration filter." : "Add your first customer login to get started."}</p>
                            {credentials.length ? (
                                <button type="button" className="btn" onClick={clearFilters}>Clear filters</button>
                            ) : <Link className="btn" to="/create">Add a credential</Link>}
                        </div>
                    ) : (
                        <div className="credentials-list">
                            {searchedCredentials.map((credential) => (
                                <CredentialItem key={credential.id} credential={credential} />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
